"use client";

import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Avatar,
  Grid,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useTheme,
  IconButton,
} from "@mui/material";
import MainCard from "ui-component/cards/MainCard";

import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { AddOutlined, RemoveOutlined } from "@mui/icons-material";
import useRequestIdGenerater from "customhooks";
import useTimeStamp from "reuseableTable/uniquetimestamp/GenerateUniqueTimeStamp";
import { useDispatch } from "store";
import axios from "axios";
import { openSnackbar } from "store/slices/snackbar";

const BecomeMentorComponent = ({
  onBack,
  alumniData = {},
  getAlumniDetails,
}) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [keywords, setKeywords] = useState([""]);
  const [mentorshipInfo, setMentorshipInfo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false); // Declare isSubmitting variable

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    expertise: "",
    experience: "",
    availability: "",
    skills: [],
  });
  const [newSkill, setNewSkill] = useState("");
  const RequestId = useRequestIdGenerater();
  const { generateTimeStamp, rendomRequestId } = useTimeStamp();
  const dispatch = useDispatch();

  const handleDialogOpen = () => {
    setDialogOpen(true);
    setKeywords([""]);
    setMentorshipInfo("");
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setKeywords([""]);
    setMentorshipInfo("");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Add a new keyword field
  const handleAddField = () => {
    setKeywords([...keywords, ""]);
  };

  // Remove a keyword field by index
  const handleRemoveField = (index) => {
    const updated = [...keywords];
    updated.splice(index, 1);
    setKeywords(updated);
  };

  // Update keyword value by index
  const handleChange = (index, value) => {
    const updated = [...keywords];
    updated[index] = value;
    setKeywords(updated);
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    const filteredKeywords = keywords.filter(
      (keyword) => keyword.trim() !== ""
    );

    const payload = {
      areaOffers: filteredKeywords,
      mentorshipInfo: mentorshipInfo || "",
    };

    axios
      .post(`/v1/mentorship/apply-for-mentor`, payload, {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem(
            "serviceToken"
          )}`,
          "Content-Type": "application/json",
          requestId: RequestId(
            Math.floor(1000 + Math.random() * 9999),
            Math.floor(1000 + Math.random() * 9999 + rendomRequestId)
          ),
        },
      })
      .then((value) => {
        getAlumniDetails();
        dispatch(
          openSnackbar({
            open: true,
            message:
              value.data.Msg || "Mentor application submitted successfully!",
            variant: "alert",
            alert: {
              color: "success",
            },
            close: false,
          })
        );
        setIsSubmitting(false);
        handleDialogClose();
        generateTimeStamp();
        // Go back to main component after successful submission
        if (onBack) onBack();
      })
      .catch((error) => {
        if (error.response?.status === 404) {
          handleDialogClose();
          setIsSubmitting(false);
          generateTimeStamp();
          dispatch(
            openSnackbar({
              open: true,
              message: error.response.data.Msg || "Service not found",
              variant: "alert",
              alert: {
                color: "error",
              },
              close: false,
            })
          );
        } else {
          setIsSubmitting(false);
          dispatch(
            openSnackbar({
              open: true,
              message: error.response?.data?.Msg || "Something went wrong",
              variant: "alert",
              alert: {
                color: "error",
              },
              close: false,
            })
          );
        }
        generateTimeStamp();
      });
  };

  const benefits = [
    {
      icon: <EmojiObjectsIcon color="primary" />,
      text: "Share your experience and insights to help others grow.",
    },
    {
      icon: <PeopleIcon color="primary" />,
      text: "Build meaningful connections and expand your professional network.",
    },
    {
      icon: <SchoolIcon color="primary" />,
      text: "Strengthen your leadership and coaching skills.",
    },
    {
      icon: <VolunteerActivismIcon color="primary" />,
      text: "Make a positive impact on someone's journey.",
    },
  ];

  const alumniName = alumniData?.name || "Alumni User";
  // const alumniTitle = alumniData?.designation || alumniData?.jobTitle || "Professional"
  const alumniImage = alumniData?.largeAvatar;

  return (
    <MainCard
      sx={{
        background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[100]})`,
        borderRadius: 3,
      }}
      title={
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {onBack && (
              <IconButton onClick={onBack} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <SupervisorAccountIcon color="primary" />
            <Typography variant="h5">Mentorship Program</Typography>
          </Stack>
        </Stack>
      }
    >
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.background.default,
              mb: 2,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                color: "text.secondary",
                lineHeight: 1.5,
              }}
            >
              A great mentor often plays a key role in every achiever's journey.
              This mentorship initiative is designed to create meaningful
              connections, where mentors can offer support, advice, and honest
              feedback. By reaching out and sharing real-world experiences, both
              mentors and mentees grow — personally and professionally.
            </Typography>
          </Paper>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Why Become a Mentor?
          </Typography>

          <List sx={{ width: "100%" }}>
            {benefits.map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  px: 2,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    transform: "translateX(4px)",
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={4}
            sx={{
              p: 2,
              borderRadius: 2,
              textAlign: "center",
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Avatar
              alt={alumniName}
              src={alumniImage}
              sx={{ width: 90, height: 90, mx: "auto", mb: 2 }}
            />
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {alumniName}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" sx={{ mb: 3 }}>
              "Ready to share your knowledge and help others grow in their
              career journey?"
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddAltIcon />}
              onClick={handleDialogOpen}
              fullWidth
              sx={{
                bgcolor: "#028943",
                "&:hover": {
                  bgcolor: "#026d35",
                },
              }}
            >
              Showcase your skills
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ backgroundColor: "#028943", color: "white", mt: -2 }}
        >
          Add Your Skills
        </DialogTitle>

        <DialogContent sx={{ padding: 3 }}>
          <Typography variant="h6" sx={{ my: 2 }}>
            Enter Keywords (Skills/Expertise)
          </Typography>

          {keywords?.map((keyword, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", mb: 2 }}
            >
              <TextField
                fullWidth
                label={`Skill/Expertise ${index + 1}`}
                value={keyword}
                onChange={(e) => handleChange(index, e.target.value)}
                placeholder="e.g., JavaScript, Project Management, Data Analysis"
              />
              {index !== 0 && (
                <IconButton
                  color="error"
                  onClick={() => handleRemoveField(index)}
                  sx={{ ml: 1 }}
                >
                  <RemoveOutlined />
                </IconButton>
              )}
              {index === keywords?.length - 1 && (
                <IconButton
                  color="secondary"
                  onClick={handleAddField}
                  sx={{ ml: 1 }}
                >
                  <AddOutlined />
                </IconButton>
              )}
            </Box>
          ))}

          <TextField
            label="Additional Notes (optional)"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            value={mentorshipInfo}
            onChange={(e) => setMentorshipInfo(e.target.value)}
            placeholder="Tell us about your mentoring experience, availability, or any specific areas you'd like to focus on..."
            sx={{ mt: 2 }}
          />
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleDialogClose}
            color="secondary"
            sx={{ fontWeight: "bold" }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            sx={{ fontWeight: "bold" }}
            disabled={isSubmitting || keywords.every((k) => k.trim() === "")}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
};

export default BecomeMentorComponent;
